/**
 * Variable Interpolation Module
 *
 * Handles replacement of {{variable}} syntax with values from different scopes.
 * Scope priority: Environment > Collection > Global
 */

import type { HttpRequest, KeyValue, Variable, Environment, Collection } from '../../stores';

// Regex to match {{variableName}} patterns
const VARIABLE_PATTERN = /\{\{([^{}]+)\}\}/g;

// Regex to match :paramName patterns in URL paths
const PATH_PARAM_PATTERN = /:([a-zA-Z_][a-zA-Z0-9_]*)/g;

/**
 * Context containing all variable scopes for interpolation
 */
export interface VariableContext {
  /** Currently active environment variables */
  environment?: Variable[];
  /** Collection-level variables (if supported) */
  collection?: Variable[];
  /** Global/workspace-level variables */
  global?: Variable[];
}

/**
 * Result of variable resolution
 */
export interface ResolvedVariable {
  /** The variable key/name */
  key: string;
  /** The resolved value */
  value: string;
  /** The scope from which the variable was resolved */
  scope: 'environment' | 'collection' | 'global' | 'unresolved';
  /** Whether the variable is marked as secret */
  isSecret: boolean;
}

/**
 * Extracts all variable placeholders from a string
 * @param text - The text to scan for variables
 * @returns Array of variable names found
 */
export function extractVariables(text: string): string[] {
  const matches = text.matchAll(VARIABLE_PATTERN);
  const variables: string[] = [];
  for (const match of matches) {
    const varName = match[1].trim();
    if (varName && !variables.includes(varName)) {
      variables.push(varName);
    }
  }
  return variables;
}

/**
 * Resolves a variable name to its value using scope priority
 * @param name - The variable name to resolve
 * @param context - The variable context with all scopes
 * @returns The resolved variable information
 */
export function resolveVariable(name: string, context: VariableContext): ResolvedVariable {
  const trimmedName = name.trim();

  // Check environment scope first (highest priority)
  if (context.environment) {
    const envVar = context.environment.find(
      v => v.key === trimmedName && v.enabled
    );
    if (envVar) {
      return {
        key: trimmedName,
        value: envVar.value,
        scope: 'environment',
        isSecret: envVar.isSecret,
      };
    }
  }

  // Check collection scope
  if (context.collection) {
    const colVar = context.collection.find(
      v => v.key === trimmedName && v.enabled
    );
    if (colVar) {
      return {
        key: trimmedName,
        value: colVar.value,
        scope: 'collection',
        isSecret: colVar.isSecret,
      };
    }
  }

  // Check global scope (lowest priority)
  if (context.global) {
    const globalVar = context.global.find(
      v => v.key === trimmedName && v.enabled
    );
    if (globalVar) {
      return {
        key: trimmedName,
        value: globalVar.value,
        scope: 'global',
        isSecret: globalVar.isSecret,
      };
    }
  }

  // Variable not found - return unresolved
  return {
    key: trimmedName,
    value: `{{${trimmedName}}}`, // Keep original placeholder
    scope: 'unresolved',
    isSecret: false,
  };
}

/**
 * Interpolates all variables in a string
 * @param text - The text containing {{variable}} placeholders
 * @param context - The variable context with all scopes
 * @returns The interpolated string
 */
export function interpolateString(text: string, context: VariableContext): string {
  if (!text) return text;

  return text.replace(VARIABLE_PATTERN, (match, varName) => {
    const resolved = resolveVariable(varName, context);
    return resolved.value;
  });
}

/**
 * Replaces path parameters in URL with their values
 * @param url - The URL containing :param placeholders
 * @param pathParams - Array of path parameter key-value pairs
 * @returns URL with path params replaced
 */
export function replacePathParams(url: string, pathParams?: KeyValue[]): string {
  if (!url || !pathParams?.length) return url;

  let result = url;
  for (const param of pathParams) {
    if (param.enabled && param.key && param.value) {
      // Replace :paramName with the value, URL-encoded
      result = result.replace(
        new RegExp(`:${param.key}(?=[/\\?#]|$)`, 'g'),
        encodeURIComponent(param.value)
      );
    }
  }
  return result;
}

/**
 * Extracts path parameter names from a URL
 * @param url - URL potentially containing :param patterns
 * @returns Array of parameter names
 */
export function extractPathParamsFromUrl(url: string): string[] {
  if (!url) return [];

  const params: string[] = [];
  let match;

  // Reset lastIndex
  PATH_PARAM_PATTERN.lastIndex = 0;

  while ((match = PATH_PARAM_PATTERN.exec(url)) !== null) {
    const paramName = match[1];
    if (!params.includes(paramName)) {
      params.push(paramName);
    }
  }

  return params;
}

/**
 * Interpolates variables in a KeyValue array (headers, params, form data)
 * @param items - Array of KeyValue items
 * @param context - The variable context
 * @returns New array with interpolated values
 */
export function interpolateKeyValues(
  items: KeyValue[],
  context: VariableContext
): KeyValue[] {
  return items.map(item => ({
    ...item,
    key: interpolateString(item.key, context),
    value: interpolateString(item.value, context),
    description: item.description ? interpolateString(item.description, context) : item.description,
  }));
}

/**
 * Interpolates all variables in an HTTP request
 * @param request - The HTTP request to interpolate
 * @param context - The variable context
 * @returns A new request object with all variables interpolated
 */
export function interpolateRequest(
  request: HttpRequest,
  context: VariableContext
): HttpRequest {
  // First interpolate variables, then replace path params
  let interpolatedUrl = interpolateString(request.url, context);

  // Replace path params (:id, :userId, etc.) with their values
  if (request.pathParams?.length) {
    interpolatedUrl = replacePathParams(interpolatedUrl, request.pathParams);
  }

  const interpolated: HttpRequest = {
    ...request,
    url: interpolatedUrl,
    headers: interpolateKeyValues(request.headers, context),
    params: interpolateKeyValues(request.params, context),
    pathParams: request.pathParams, // Keep path params as-is
  };

  // Interpolate body if present
  if (request.body) {
    interpolated.body = { ...request.body };

    if (request.body.raw?.content) {
      interpolated.body.raw = {
        ...request.body.raw,
        content: interpolateString(request.body.raw.content, context),
      };
    }

    if (request.body.formData) {
      interpolated.body.formData = interpolateKeyValues(request.body.formData, context);
    }
  }

  // Interpolate auth if present
  if (request.auth) {
    interpolated.auth = { ...request.auth };

    if (request.auth.bearer?.token) {
      interpolated.auth.bearer = {
        token: interpolateString(request.auth.bearer.token, context),
      };
    }

    if (request.auth.basic) {
      interpolated.auth.basic = {
        username: interpolateString(request.auth.basic.username, context),
        password: interpolateString(request.auth.basic.password, context),
      };
    }

    if (request.auth.apiKey) {
      interpolated.auth.apiKey = {
        ...request.auth.apiKey,
        key: interpolateString(request.auth.apiKey.key, context),
        value: interpolateString(request.auth.apiKey.value, context),
      };
    }

    // OAuth2 - interpolate URLs and credentials
    if (request.auth.oauth2) {
      interpolated.auth.oauth2 = {
        ...request.auth.oauth2,
        accessTokenUrl: interpolateString(request.auth.oauth2.accessTokenUrl, context),
        clientId: interpolateString(request.auth.oauth2.clientId, context),
        clientSecret: request.auth.oauth2.clientSecret
          ? interpolateString(request.auth.oauth2.clientSecret, context)
          : undefined,
        scope: request.auth.oauth2.scope
          ? interpolateString(request.auth.oauth2.scope, context)
          : undefined,
        // Also interpolate username/password for password grant type
        username: request.auth.oauth2.username
          ? interpolateString(request.auth.oauth2.username, context)
          : undefined,
        password: request.auth.oauth2.password
          ? interpolateString(request.auth.oauth2.password, context)
          : undefined,
      };
    }

    // HAWK - interpolate credentials
    if (request.auth.hawk) {
      interpolated.auth.hawk = {
        ...request.auth.hawk,
        authId: interpolateString(request.auth.hawk.authId, context),
        authKey: interpolateString(request.auth.hawk.authKey, context),
        ext: request.auth.hawk.ext
          ? interpolateString(request.auth.hawk.ext, context)
          : undefined,
      };
    }
  }

  return interpolated;
}

/**
 * Builds a variable context from the current state
 * @param activeEnvironment - The currently selected environment
 * @param globalEnvironment - The global environment (if any)
 * @param collectionVariables - Collection-level variables (if supported)
 * @returns A complete variable context for interpolation
 */
export function buildVariableContext(
  activeEnvironment?: Environment | null,
  globalEnvironment?: Environment | null,
  collectionVariables?: Variable[]
): VariableContext {
  return {
    environment: activeEnvironment?.variables,
    collection: collectionVariables,
    global: globalEnvironment?.variables,
  };
}

/**
 * Gets all unresolved variables from a request
 * Useful for showing warnings to users about missing variables
 * @param request - The HTTP request to check
 * @param context - The variable context
 * @returns Array of unresolved variable names
 */
export function getUnresolvedVariables(
  request: HttpRequest,
  context: VariableContext
): string[] {
  const allText = [
    request.url,
    ...request.headers.flatMap(h => [h.key, h.value]),
    ...request.params.flatMap(p => [p.key, p.value]),
    request.body?.raw?.content || '',
    ...(request.body?.formData?.flatMap(f => [f.key, f.value]) || []),
    request.auth?.bearer?.token || '',
    request.auth?.basic?.username || '',
    request.auth?.basic?.password || '',
    request.auth?.apiKey?.key || '',
    request.auth?.apiKey?.value || '',
  ].filter(Boolean).join(' ');

  const variables = extractVariables(allText);

  return variables.filter(name => {
    const resolved = resolveVariable(name, context);
    return resolved.scope === 'unresolved';
  });
}

/**
 * Previews a variable value with optional masking for secrets
 * @param name - The variable name
 * @param context - The variable context
 * @param maskSecrets - Whether to mask secret values
 * @returns The preview string with scope information
 */
export function previewVariable(
  name: string,
  context: VariableContext,
  maskSecrets: boolean = true
): { value: string; scope: string; isSecret: boolean } {
  const resolved = resolveVariable(name, context);

  let displayValue = resolved.value;
  if (resolved.isSecret && maskSecrets && resolved.scope !== 'unresolved') {
    displayValue = '••••••••';
  }

  return {
    value: displayValue,
    scope: resolved.scope,
    isSecret: resolved.isSecret,
  };
}
